import {
  toLineBreak,
  toLinkContent,
  toParagraphContent,
} from '@principle-theorem/editor';
import {
  MarketingCampaign,
  MarketingCampaignStep,
  Practice,
  RecurrencePattern,
} from '@principle-theorem/principle-core';
import {
  IPractice,
  MarketingCampaignStepConversionType,
  MarketingCampaignType,
  TemplateScope,
  TimingDirection,
  TimingUnit,
} from '@principle-theorem/principle-core/interfaces';
import { FactTables, recallReportTemplate } from '@principle-theorem/reporting';
import {
  ALL_REGIONS,
  asReffable,
  CustomRecurrenceFrequency,
  DayOfWeek,
  DocumentReference,
  Frequency,
} from '@principle-theorem/shared';
import { CampaignTemplateHelpers } from './campaign-template-helpers';
import { IMarketingCampaignTemplate } from './marketing-campaign-template';
import { MarketingCampaignTemplateFactory } from './marketing-campaign-template-factory';

export function getRecallCampaignTemplate(
  practiceRef: DocumentReference<IPractice>
): IMarketingCampaignTemplate {
  return {
    name: 'Recall Campaign',
    description:
      'Automatically remind patients to book their next hygiene appointment.',
    regions: ALL_REGIONS,
    campaign: MarketingCampaign.init({
      brandRef: Practice.brandRef(asReffable(practiceRef)),
      practiceRef,
      name: 'Recall Campaign',
      type: MarketingCampaignType.GeneralNotice,
      scope: TemplateScope.Patient,
      maximumEntriesPerRun: 100,
      // TODO: Doesn't this mean that once converted, the patient will never be able to re-enter?
      entryResetRules: {
        multipleEntry: true,
        reentryScope: TemplateScope.Patient,
        maxEntryCount: undefined,
        frequencyCount: 3,
        frequencyType: CustomRecurrenceFrequency.Monthly,
      },
      recurrencePattern: RecurrencePattern.init({
        customFrequencyType: CustomRecurrenceFrequency.Weekly,
        daysOfWeek: [
          DayOfWeek.Monday,
          DayOfWeek.Tuesday,
          DayOfWeek.Wednesday,
          DayOfWeek.Thursday,
          DayOfWeek.Friday,
        ],
        frequencyType: Frequency.Custom,
      }),
      recurrenceRunTime: '09:04',
      conversionType: MarketingCampaignStepConversionType.AnyAppointmentBooked,
    }),
    report: recallReportTemplate(FactTables.patientEvent.latestEvent),
    steps: [
      MarketingCampaignStep.init({
        name: 'Upcoming Recall Due SMS',
        config: MarketingCampaignTemplateFactory.automationSMS(
          [
            toParagraphContent(
              [
                'Hi {{ patientFirstName }}! 👋 It’s almost time for your regular check-up & clean at {{ practiceName }}.',
                'These visits help prevent small issues from turning into big ones (and keep your smile looking great!).',
                toLineBreak(),
                'Secure your preferred time before the diary fills up 👉 {{ appointmentBookingLink }}',
              ],
              true
            ),
          ],
          {
            amount: 1,
            direction: TimingDirection.After,
            unit: TimingUnit.Minutes,
          },
          MarketingCampaignTemplateFactory.conditions([
            MarketingCampaignTemplateFactory.conditionHasMobile(true),
          ])
        ),
      }),
      MarketingCampaignStep.init({
        name: 'Upcoming Recall Due Email (Backup)',
        config: MarketingCampaignTemplateFactory.automationEmail(
          'Time to book your next check-up & clean 🦷',
          [
            toParagraphContent(
              [
                'Hi {{ patientFirstName }},',
                toLineBreak(),
                'Your regular check-up and professional clean is coming up next month. These visits are one of the most effective ways to:',
                '- Catch small problems before they become big (and expensive) ones',
                '- Remove tartar build-up your toothbrush can’t reach',
                '- Protect against gum disease, decay, and even health conditions linked to oral bacteria',
                toLineBreak(),
                [
                  'Even with daily brushing and flossing, some plaque and bacteria hide in hard-to-reach places. That’s why professional cleans are essential - your toothbrush can’t remove hardened tartar. ',
                  CampaignTemplateHelpers.highlight(
                    '[INSERT BLOG OR FAQ FROM WEBSITE]'
                  ),
                ],
                toLineBreak(),
                '📅 Book ahead now to secure your preferred time:',
                ['👉 ', toLinkContent('{{ appointmentBookingLink }}')],
                [
                  '📞 Or call us on ',
                  CampaignTemplateHelpers.phoneLink(
                    '{{ practicePhoneNumber }}'
                  ),
                ],
                toLineBreak(),
                'Warm Regards,',
                'The {{ practiceName }} Team',
                toLineBreak(),
                CampaignTemplateHelpers.highlight('[INSERT PRACTICE LOGO]'),
              ],
              true
            ),
          ],
          {
            amount: 3,
            direction: TimingDirection.After,
            unit: TimingUnit.Hours,
          },
          MarketingCampaignTemplateFactory.conditions([
            MarketingCampaignTemplateFactory.conditionHasMobile(false),
            MarketingCampaignTemplateFactory.conditionHasEmail(true),
          ])
        ),
      }),
      MarketingCampaignStep.init({
        name: 'Recall Due Now SMS',
        config: MarketingCampaignTemplateFactory.automationSMS(
          [
            toParagraphContent(
              [
                'Hey {{ patientFirstName }}, it’s time for your regular check-up & clean with {{ practiceName }}🦷',
                'Even with good brushing, only a professional clean removes tartar and bacteria that cause decay and gum issues.',
                toLineBreak(),
                'Book your visit today 👉 {{ appointmentBookingLink }}',
              ],
              true
            ),
          ],
          {
            amount: 27,
            direction: TimingDirection.After,
            unit: TimingUnit.Days,
            time: '08:44',
          },
          MarketingCampaignTemplateFactory.conditions([
            MarketingCampaignTemplateFactory.conditionHasMobile(true),
          ])
        ),
      }),
      MarketingCampaignStep.init({
        name: 'Recall Due Now Email (Backup)',
        config: MarketingCampaignTemplateFactory.automationEmail(
          'Healthy smiles don’t wait – let’s see you soon',
          [
            toParagraphContent(
              [
                'Hi {{ patientFirstName }},',
                toLineBreak(),
                'It’s time! Your check-up and clean is now due. Keeping up with regular visits helps you:',
                '- Stay ahead of gum disease (linked to heart and lung health, and even memory conditions)',
                '- Prevent tooth loss, cavities, and infections',
                '- Keep your smile feeling fresh and looking bright',
                toLineBreak(),
                [
                  'Did you know? Skipping professional cleans leaves tartar behind that can’t be removed at home. Our FAQ explains why 👉 ',
                  CampaignTemplateHelpers.highlight(
                    '[INSERT BLOG OR FAQ FROM WEBSITE]'
                  ),
                ],
                toLineBreak(),
                '📅 Book ahead now to secure your preferred time:',
                ['👉 ', toLinkContent('{{ appointmentBookingLink }}')],
                [
                  '📞 Or call us on ',
                  CampaignTemplateHelpers.phoneLink(
                    '{{ practicePhoneNumber }}'
                  ),
                ],
                toLineBreak(),
                'Warm Regards,',
                'The {{ practiceName }} Team',
                toLineBreak(),
                CampaignTemplateHelpers.highlight('[INSERT PRACTICE LOGO]'),
              ],
              true
            ),
          ],
          {
            amount: 2,
            direction: TimingDirection.After,
            unit: TimingUnit.Hours,
          },
          MarketingCampaignTemplateFactory.conditions([
            MarketingCampaignTemplateFactory.conditionHasEmail(true),
            MarketingCampaignTemplateFactory.conditionHasMobile(false),
          ])
        ),
      }),
      MarketingCampaignStep.init({
        name: 'Recall Due Now SMS',
        config: MarketingCampaignTemplateFactory.automationSMS(
          [
            toParagraphContent(
              [
                'Hi {{ patientFirstName }}, just checking in! 😊',
                'We still have a few appointments available for your check-up & clean.',
                'Give us a call on {{ practicePhoneNumber }} or pop online to book before they fill up 👉 {{ appointmentBookingLink }}',
              ],
              true
            ),
          ],
          {
            amount: 1,
            direction: TimingDirection.After,
            unit: TimingUnit.Weeks,
            time: '16:51',
          },
          MarketingCampaignTemplateFactory.conditions([
            MarketingCampaignTemplateFactory.conditionHasMobile(true),
          ])
        ),
      }),
      MarketingCampaignStep.init({
        name: 'Overdue Recall SMS',
        config: MarketingCampaignTemplateFactory.automationSMS(
          [
            toParagraphContent(
              [
                'Hi {{ patientFirstName }}, we’ve missed you! 💙',
                'Your check-up & clean is now overdue.',
                'A quick visit helps remove tartar, keep gums healthy, and spot early issues before they cause trouble.',
                toLineBreak(),
                'Let’s get you back on track 👉 {{ appointmentBookingLink }}',
              ],
              true
            ),
          ],
          {
            amount: 29,
            direction: TimingDirection.After,
            unit: TimingUnit.Days,
            time: '08:34',
          },
          MarketingCampaignTemplateFactory.conditions([
            MarketingCampaignTemplateFactory.conditionHasMobile(true),
          ])
        ),
      }),
      MarketingCampaignStep.init({
        name: 'Overdue Recall Email (Backup)',
        config: MarketingCampaignTemplateFactory.automationEmail(
          'We’ve missed you – your check-up is overdue',
          [
            toParagraphContent(
              [
                'Hi {{ patientFirstName }},',
                toLineBreak(),
                'We noticed you haven’t had your recall check-up and clean yet, and it’s now overdue.',
                toLineBreak(),
                'Even though life gets busy, delaying professional care increases your risk of gum disease, decay, and even wider health problems. Studies link gum disease to heart and lung complications, low birth weight, and certain types of dementia.',
                toLineBreak(),
                'It’s never too late to get back on track! Let’s book your appointment today so we can:',
                '- Remove tartar build-up your toothbrush can’t touch',
                '- Protect your gums and teeth for the long term',
                '- Give you peace of mind with a professional oral health check',
                toLineBreak(),
                '📅 Book your appointment with the below link:',
                ['👉 ', toLinkContent('{{ appointmentBookingLink }}')],
                [
                  '📞 Or call us on ',
                  CampaignTemplateHelpers.phoneLink(
                    '{{ practicePhoneNumber }}'
                  ),
                ],
                toLineBreak(),
                'Warm Regards,',
                'The {{ practiceName }} Team',
                toLineBreak(),
                CampaignTemplateHelpers.highlight('[INSERT PRACTICE LOGO]'),
              ],
              true
            ),
          ],
          {
            amount: 3,
            direction: TimingDirection.After,
            unit: TimingUnit.Hours,
          },
          MarketingCampaignTemplateFactory.conditions([
            MarketingCampaignTemplateFactory.conditionHasMobile(false),
            MarketingCampaignTemplateFactory.conditionHasEmail(true),
          ])
        ),
      }),
      MarketingCampaignStep.init({
        name: 'Overdue Recall email - Final Reminder',
        config: MarketingCampaignTemplateFactory.automationSMS(
          [
            toParagraphContent(
              [
                'Hi {{ patientFirstName }}, we’d love to see you soon! 💙',
                'Your check-up & clean is still overdue, let’s get you back on track for a healthy smile.',
                toLineBreak(),
                'Book online anytime 👉 {{ appointmentBookingLink }} or call us on {{ practicePhoneNumber }}',
              ],
              true
            ),
          ],
          {
            amount: 1,
            direction: TimingDirection.After,
            unit: TimingUnit.Weeks,
            time: '17:07',
          },
          MarketingCampaignTemplateFactory.conditions([
            MarketingCampaignTemplateFactory.conditionHasMobile(true),
          ])
        ),
      }),
    ],
  };
}
